import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  BookmarkIcon,
  DownloadIcon,
  FileTextIcon,
  FlagIcon,
  FolderIcon,
} from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { Breadcrumbs, type Crumb } from "@/components/layout/Breadcrumbs";
import { Badge } from "@/components/ledger/Badge";
import { useAuth } from "@/lib/auth-context";
import {
  useBookmarks,
  useDirectory,
  useDirectoryFiles,
  useModules,
  useSubject,
  useToggleBookmark,
} from "@/lib/query-hooks";
import type { FileItem } from "@/lib/types";

export const Route = createFileRoute(
  "/exam/$examSlug/subject/$subjectSlug/$moduleKey/$",
)({
  head: ({ params }) => ({
    meta: [
      {
        title: `${params.moduleKey} · ${params.subjectSlug} — PN-CivilServices`,
      },
      {
        name: "description",
        content: `Browse ${params.moduleKey} files for ${params.subjectSlug} in ${params.examSlug.toUpperCase()}.`,
      },
      {
        property: "og:title",
        content: `${params.moduleKey} · ${params.subjectSlug}`,
      },
      {
        property: "og:description",
        content: `Downloadable ${params.moduleKey} for ${params.subjectSlug}.`,
      },
    ],
  }),
  component: ModuleBrowser,
});

function ModuleBrowser() {
  const { examSlug, subjectSlug, moduleKey, _splat } = Route.useParams();
  const folderPath = (_splat || "").split("/").filter(Boolean);
  const { data: subject } = useSubject(examSlug, subjectSlug);
  const { data: modules } = useModules(subject?.id);
  const rootDirId = modules?.find((m) => m.key === moduleKey)?.rootDirectoryId;

  // Simple path-resolution stub: for now the deepest directory ID is passed
  // as the last segment when the backend returns slug-paths. Until the backend
  // exists we simply look up the module root — extend `useDirectory` to walk
  // the tree when real endpoints are wired.
  const directoryId = folderPath[folderPath.length - 1] || rootDirId;
  const { data: dir } = useDirectory(directoryId);
  const { data: filePages, fetchNextPage, hasNextPage, isFetching } =
    useDirectoryFiles(directoryId, {});
  const files = filePages?.pages.flatMap((p) => p.items) ?? [];

  const crumbs: Crumb[] = useMemo(() => {
    const base: Crumb[] = [
      { label: "Ledger", to: "/" },
      { label: examSlug.toUpperCase(), to: `/exam/${examSlug}` },
      {
        label: subject?.name ?? subjectSlug,
        to: `/exam/${examSlug}/subject/${subjectSlug}`,
      },
      { label: prettyModule(moduleKey) },
    ];
    for (const c of dir?.breadcrumbs ?? []) base.push({ label: c.name });
    return base;
  }, [examSlug, subjectSlug, moduleKey, subject, dir]);

  const [preview, setPreview] = useState<FileItem | null>(null);

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl px-4 pt-8 pb-16 sm:px-6 lg:px-8">
        <Breadcrumbs items={crumbs} />

        <header className="mt-8 mb-6 flex items-end justify-between">
          <div>
            <div className="label-card">Module</div>
            <h1 className="mt-1 font-display text-4xl font-semibold">
              {prettyModule(moduleKey)}
            </h1>
            <p className="mt-2 text-ink-soft">
              {examplesFor(moduleKey)}
            </p>
          </div>
          <div className="hidden md:block text-xs text-ink-muted">
            {files.length} of {dir?.fileCount ?? files.length} files
          </div>
        </header>

        {/* Folders */}
        {dir?.children?.length ? (
          <div className="mb-8">
            <div className="label-card mb-3">Folders</div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {dir.children.map((child) => (
                <Link
                  key={child.id}
                  to="/exam/$examSlug/subject/$subjectSlug/$moduleKey/$"
                  params={{
                    examSlug,
                    subjectSlug,
                    moduleKey,
                    _splat: [...folderPath, child.slug].join("/"),
                  }}
                  className="card-ledger flex items-center gap-3 p-4 hover:border-paprika transition"
                >
                  <FolderIcon className="h-5 w-5 text-paprika" />
                  <div className="flex-1">
                    <div className="font-semibold">{child.name}</div>
                    <div className="text-xs text-ink-muted">
                      {child.fileCount ?? 0} files
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : null}

        {/* Files */}
        <div className="label-card mb-3">Files</div>
        <ul className="card-ledger divide-y divide-hairline overflow-hidden">
          {files.length === 0 && !isFetching ? (
            <li className="p-10 text-center text-ink-soft">
              No files here yet — check back later.
            </li>
          ) : (
            files.map((f) => (
              <FileRow key={f.id} file={f} onPreview={setPreview} />
            ))
          )}
        </ul>

        {hasNextPage ? (
          <div className="mt-4 text-center">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetching}
              className="rounded-md border border-hairline bg-surface px-4 py-2 text-sm hover:bg-surface-alt"
            >
              {isFetching ? "Loading…" : "Load more"}
            </button>
          </div>
        ) : null}
      </div>

      {preview ? (
        <FilePreviewPanel file={preview} onClose={() => setPreview(null)} />
      ) : null}
    </AppShell>
  );
}

function FileRow({
  file,
  onPreview,
}: {
  file: FileItem;
  onPreview: (f: FileItem) => void;
}) {
  const { user } = useAuth();
  const { data: bookmarks } = useBookmarks();
  const bookmarked = !!bookmarks?.find((b) => b.fileId === file.id);
  const toggle = useToggleBookmark();

  return (
    <li className="flex items-center gap-4 px-5 py-3 hover:bg-surface-alt/40 transition">
      <FileTextIcon className="h-5 w-5 text-ink-soft shrink-0" />
      <button
        onClick={() => onPreview(file)}
        className="flex-1 text-left"
      >
        <div className="font-medium">{file.title ?? file.name}</div>
        <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-ink-muted">
          {file.year ? <span>Year {file.year}</span> : null}
          <span>{formatBytes(file.sizeBytes)}</span>
          <span>{(file.mimeType || "").split("/")[1]?.toUpperCase()}</span>
          {file.isNew ? <Badge variant="new">New</Badge> : null}
        </div>
      </button>
      {user ? (
        <button
          aria-label="Bookmark"
          onClick={() =>
            toggle.mutate({ fileId: file.id, bookmarked })
          }
          className={`grid h-8 w-8 place-items-center rounded-md hover:bg-surface-alt ${
            bookmarked ? "text-paprika" : "text-ink-muted"
          }`}
        >
          <BookmarkIcon
            className="h-4 w-4"
            fill={bookmarked ? "currentColor" : "none"}
          />
        </button>
      ) : null}
      {file.downloadUrl ? (
        <a
          href={file.downloadUrl}
          className="grid h-8 w-8 place-items-center rounded-md text-ink-soft hover:text-paprika hover:bg-surface-alt"
          aria-label="Download"
        >
          <DownloadIcon className="h-4 w-4" />
        </a>
      ) : null}
    </li>
  );
}

function FilePreviewPanel({
  file,
  onClose,
}: {
  file: FileItem;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="card-ledger w-full max-w-3xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hairline-b flex items-start justify-between p-5">
          <div>
            <div className="label-card">File</div>
            <h2 className="font-display text-2xl font-semibold mt-1">
              {file.title ?? file.name}
            </h2>
            <div className="mt-1 text-xs text-ink-muted">
              {formatBytes(file.sizeBytes)} · {file.mimeType}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-paprika text-sm"
          >
            Close
          </button>
        </div>
        <div className="p-5 space-y-4">
          {file.previewUrl ? (
            <iframe
              src={file.previewUrl}
              className="w-full h-[60vh] rounded-md border border-hairline"
              title={file.name}
            />
          ) : (
            <div className="p-10 text-center bg-surface-alt/50 rounded-md text-ink-soft text-sm">
              Preview not available for this file type.
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {file.downloadUrl ? (
              <a
                href={file.downloadUrl}
                className="inline-flex items-center gap-2 rounded-md bg-paprika px-4 py-2 text-sm font-semibold uppercase tracking-widest text-white"
              >
                <DownloadIcon className="h-4 w-4" /> Download
              </a>
            ) : null}
            <button className="inline-flex items-center gap-2 rounded-md border border-hairline px-4 py-2 text-sm hover:bg-surface-alt">
              <FlagIcon className="h-4 w-4" /> Report an issue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function prettyModule(k: string) {
  return {
    pyqs: "PYQs",
    ncerts: "NCERTs",
    "standard-books": "Standard Books",
    "study-materials": "Study Materials",
    "handwritten-notes": "Handwritten Notes",
  }[k] ?? k;
}

function examplesFor(k: string) {
  return {
    pyqs: "Filed by year — the exam year the paper is from.",
    ncerts: "Filed by class — Class VI through Class XII.",
    "standard-books": "Curated reference titles, by author.",
    "study-materials": "Coaching handouts, compilations, current affairs.",
    "handwritten-notes": "Topper and personal notes, subject-wise.",
  }[k] ?? "";
}

function formatBytes(b: number) {
  if (!b) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = b;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}
